import { useEffect, useState } from "react";
import { Github, Twitter, Linkedin, Dribbble } from "lucide-react";
import { handleAnchorClick } from "@/lib/scroll";

const NAV_LINKS = [
    { label: "Home", href: "#home" },
    { label: "Services", href: "#services" },
    { label: "Pricing", href: "#pricing" },
    { label: "About", href: "#about" },
    { label: "Contact", href: "#contact" },
];

const SOCIALS = [
    { label: "GitHub", Icon: Github, testId: "footer-social-github" },
    { label: "Twitter", Icon: Twitter, testId: "footer-social-twitter" },
    { label: "LinkedIn", Icon: Linkedin, testId: "footer-social-linkedin" },
    { label: "Dribbble", Icon: Dribbble, testId: "footer-social-dribbble" },
];

const Footer = () => {
    const [time, setTime] = useState("");

    useEffect(() => {
        const tick = () =>
            setTime(
                new Date().toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                })
            );
        tick();
        const id = setInterval(tick, 30000);
        return () => clearInterval(id);
    }, []);

    return (
        <footer data-testid="site-footer" className="border-t border-slate-200 bg-white">
            <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
                <div className="flex flex-col gap-10 md:flex-row md:items-start md:justify-between">
                    <div className="max-w-sm">
                        <div className="flex items-center gap-2.5">
                            <img
                                src="/logo-mark.png"
                                alt="CS Studio logo"
                                className="h-9 w-9 object-contain"
                            />
                            <span className="text-[15px] font-bold tracking-tight text-ink">
                                CS Studio
                            </span>
                        </div>
                        <p className="mt-4 text-sm leading-relaxed text-slate-500">
                            Custom websites designed and built by one person who
                            cares about every pixel.
                        </p>
                        <div className="mt-6 flex gap-2.5">
                            {SOCIALS.map(({ label, Icon, testId }) => (
                                <a
                                    key={label}
                                    href="#"
                                    data-testid={testId}
                                    aria-label={`${label} (placeholder)`}
                                    onClick={(e) => e.preventDefault()}
                                    className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition-colors duration-200 hover:border-brand/40 hover:bg-ice hover:text-brand-dark"
                                >
                                    <Icon className="h-4 w-4" />
                                </a>
                            ))}
                        </div>
                    </div>

                    <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                            Navigate
                        </p>
                        <ul className="mt-4 space-y-2.5">
                            {NAV_LINKS.map((link) => (
                                <li key={link.href}>
                                    <a
                                        href={link.href}
                                        data-testid={`footer-link-${link.label.toLowerCase()}`}
                                        onClick={(e) => handleAnchorClick(e, link.href)}
                                        className="text-sm text-slate-600 transition-colors duration-200 hover:text-brand-dark"
                                    >
                                        {link.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                            Contact
                        </p>
                        <a
                            href="mailto:csstudiowebdesign21@gmail.com"
                            data-testid="footer-email"
                            className="mt-4 block text-sm font-medium text-ink transition-colors duration-200 hover:text-brand-dark"
                        >
                            csstudiowebdesign21@gmail.com
                        </a>
                        <p
                            data-testid="footer-local-time"
                            className="mt-3 font-mono text-xs text-slate-400"
                        >
                            Local time {time} · Working worldwide
                        </p>
                    </div>
                </div>

                <div className="mt-12 flex flex-col items-start justify-between gap-3 border-t border-slate-100 pt-7 sm:flex-row sm:items-center">
                    <p className="text-xs text-slate-400">
                        © 2026 CS Studio. All rights reserved.
                    </p>
                    <p className="font-mono text-xs text-slate-300">
                        Designed and built by hand. No templates.
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
